import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ticketContactsQuery, type TicketPerson } from '../api/easeedesk'
import { Spinner } from '../ui/Spinner'
import { EscalateButton, TicketEscalateModal, type EscalateTarget } from './TicketEscalateModal'
import { assetUrl } from '../ui/assetUrl'
import './TicketContacts.css'

/**
 * Fila "Contacts" - `easeedesk/tickets/contacts/index.php`.
 *
 * Cate o fila mica pentru fiecare departament al clientului, apoi "Oncall",
 * "Team", "IT Manager", "Critical notifications" si "Vips". Grupurile se
 * socotesc pe server; macheta veche le scotea din aceeasi lista cu trei bucle
 * imbricate, si a doua se rupsese - un departament cu doi oameni deschidea doua
 * panouri cu acelasi id, si al doilea ramanea gol.
 *
 * Butonul "Escalate" e pe oamenii de garda si pe cei din echipa (nu pe
 * manager, contacte critice sau VIP - asa e macheta), numai cat tichetul e
 * deschis; deschide `TicketEscalateModal`, ca in portal.
 */

const DEFAULT_AVATAR = assetUrl('images/default_user.png')

export function TicketContacts({ id }: { id: number }) {
  const { data, error } = useQuery(ticketContactsQuery(id))
  const [open, setOpen] = useState(0)
  const [target, setTarget] = useState<EscalateTarget | null>(null)

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const everyone = data.departments.flatMap((d) => d.people)

  const panes: { name: string; people: TicketPerson[]; empty: string; escalate: boolean }[] = [
    ...data.departments.map((d) => ({ name: d.name, people: d.people, empty: '', escalate: false })),
    { name: 'Oncall', people: data.oncall, empty: 'No oncall users found', escalate: true },
    { name: 'Team', people: everyone, empty: 'No team members for this company', escalate: true },
    { name: 'IT Manager', people: data.manager ? [data.manager] : [], empty: 'No IT Manager for this company', escalate: false },
    { name: 'Critical notifications', people: data.critical, empty: 'No CNR for this company', escalate: false },
    { name: 'Vips', people: data.vips, empty: 'No VIP for this company', escalate: false },
  ]

  const current = panes[Math.min(open, panes.length - 1)]

  return (
    <div id={`ctTab${id}`}>
      <ul className="nav nav-tabs ct-tabs" role="tablist">
        {panes.map((pane, i) => (
          <li key={pane.name} role="presentation" className={i === open ? 'active' : undefined}>
            <a onClick={() => setOpen(i)}>{pane.name}</a>
          </li>
        ))}
      </ul>

      <div className="ct-pane">
        {current.people.length === 0 ? (
          <p className="ct-none">{current.empty}</p>
        ) : (
          current.people.map((person, i) => (
            <Card key={`${person.id}-${i}`} person={person} escalate={current.escalate && data.open ? { ticket: id, onPick: setTarget } : null} />
          ))
        )}
      </div>

      <TicketEscalateModal ticket={id} target={target} onClose={() => setTarget(null)} />
    </div>
  )
}

function Card({ person, escalate }: { person: TicketPerson; escalate: { ticket: number; onPick(t: EscalateTarget): void } | null }) {
  return (
    <div className="ct-box">
      {escalate && (
        <div className="fast_dispatch_actions">
          <EscalateButton ticket={escalate.ticket} person={{ user: person.id, group: person.groupId ?? 0, name: person.name }} onPick={escalate.onPick} />
        </div>
      )}
      <img className="ct-avatar" src={person.image !== '' ? `/application/views/hr/files/poza/${person.image}` : DEFAULT_AVATAR} alt="" />
      <div className="ct-details">
        <p className="ct-name">
          <strong>{person.name}</strong>
        </p>
        {person.department !== '' && (
          <p>
            <i className="fa fa-users"></i> {person.department}
          </p>
        )}
        <p>
          <i className="fa fa-phone"></i> {person.phone !== '' ? person.phone : '-'}
        </p>
        {/* Telefonul de garda si cate tichete deschise are: al doilea e chiar
            motivul pentru care cineva alege intre doi oameni de garda. */}
        {person.oncallPhone !== undefined && (
          <p>
            <i className="fas fa-phone-volume"></i> {person.oncallPhone !== '' ? person.oncallPhone : '-'}
            {person.tickets !== undefined && (
              <span className="badge ct-tickets">
                <i className="fas fa-ticket-alt"></i> {person.tickets}
              </span>
            )}
          </p>
        )}
        {person.email !== '' && (
          <p>
            <i className="fa fa-envelope"></i>{' '}
            <a href={`mailto:${person.email}`}>{person.email}</a>
          </p>
        )}
      </div>
    </div>
  )
}
